// A KÖZPONTI (multi-app) admin-konzol app-választójának adata + állapota. Az Odin `GET /v1/admin/apps` proxyból
// tölt, ami **platform:admin VAGY `rbac:admin`** jogot kér (a `rbac:admin` a least-privilege, kereszt-app RBAC-
// konzol-jog — a superadmin szerep hordozza); egyik nélkül a BFF 403-at ad → üres lista → a választó REJTVE marad,
// és minden oldal a saját appjára (atlas) esik vissza. Így AMÍG CSAK AZ ATLAS APP LÉTEZIK (vagy nincs RBAC-konzol-
// jog), a 3 admin-oldal PONTOSAN úgy néz ki, mint eddig — az app-réteg csak több regisztrált appnál jelenik meg.
import { useMemo, useState } from 'react';

import { api } from '@/lib/api';

/** A konzolt futtató (saját) app kulcsa — a BFF is erre esik vissza, ha nincs `?app=`. */
export const DEFAULT_APP = 'atlas';

export interface AdminApp {
  key: string;
  name: string;
  /** `consumer` (spoke — CSAK ezt listázza a választó) · `platform` (Odin-governance) · `sample` (dev/teszt). */
  kind: string;
  namespaces: string[];
  status: string;
  memberCount: number | null;
}

export interface UseAdminApps {
  apps: AdminApp[];
  /** A jelenleg kezelt app kulcsa (a választó rejtettségekor mindig a DEFAULT_APP). */
  selectedApp: string;
  setSelectedApp: (key: string) => void;
  /** Csak TÖBB kezelhető appnál (platform:admin / rbac:admin) igaz → ekkor jelenik meg a választó. */
  showSelector: boolean;
}

export function useAdminApps(): UseAdminApps {
  const appsQ = api.adminApps.useQuery(['admin-apps'], {});
  const apps: AdminApp[] = useMemo(
    // A választó KIZÁRÓLAG a `consumer` appokat listázza — adat-vezérelt (az Odin `App.kind`-je alapján):
    //   • `sample` (demo/teszt): sose kell,
    //   • `platform` (maga az Odin): NEM konzol-alany. A platform GOVERNANCE-szerepei nem az Atlas
    //     jogosultság-oldalán kezelendők (ott a consumer appok feature-jogai élnek), és a választóban csak
    //     félrevezető volt: „Odin" néven egy olyan app jelent meg, aminek nincs Atlas-felhasználója.
    // MIÉRT ÍGY (és nem kulcs-alapú rejtéssel): a `key === 'odin'` egy MÁGIKUS KONSTANS lenne a UI-ban; a
    // `kind` viszont a registry saját, adat-szintű besorolása — új platform-app is automatikusan kimarad.
    () =>
      appsQ.data?.status === 200 ? appsQ.data.body.apps.filter((a) => a.kind === 'consumer') : [],
    [appsQ.data],
  );
  const [selectedApp, setSelectedApp] = useState<string>(DEFAULT_APP);
  // A VÁLASZTÓ A LISTA HOSSZÁBÓL következik, és ez nem kényelmi heurisztika: a szerver már csak azokat
  // az appokat adja vissza, amiket a hívó TÉNYLEGESEN kezelhet (Odin `listApps` — platform:admin /
  // rbac:admin / `{ns}:admin`). Egy app → nincs mire váltani. Ezért NEM szabad a választót az Atlas
  // feature-jogához (`access_rights.manage`) kötni: az a LAPOT nyitja, a Hubról semmit nem mond, és
  // abból vezetve hamis affordanciát kínálnánk.
  const showSelector = apps.length > 1;
  // A választó rejtettségekor MINDIG a saját app (atlas) — így a lekérdezések app-paramétere stabil marad.
  return {
    apps,
    selectedApp: showSelector ? selectedApp : DEFAULT_APP,
    setSelectedApp,
    showSelector,
  };
}
